import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { IntentService } from "@tylertech/intents-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { IInfoCardData, IInfoCardDataElement, IIntent } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

export interface CommentDialogData
{
    readonly label: string;
    readonly nextIntent?: IIntent;
}
export const DIALOG_CANCEL = "DIALOG_CANCEL";

@Component({
    selector: "comment-dialog-component",
    templateUrl: "./commentDialog.tmpl.html",
    standalone: false
})
export class CommentDialogComponent
    implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<CommentDialogComponent>>(MatDialogRef);
    private readonly data = inject<CommentDialogData>(MAT_DIALOG_DATA);
    private readonly $intentService = inject(IntentService);

    private readonly destructor$ = new Subject<void>();
    readonly formGroup: FormGroup;
    readonly commands: ReadonlyArray<Command>;

    protected savingPromise?: Promise<void>;

    comment: string;
    isProcessing: boolean;

    constructor()
    {
        this.formGroup = new FormGroup({
            comment: LgdFormControlFactory.textarea(undefined, false, { rows: 3 })
        });

        this.commands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.close(),
                // telemetry: "Submission-CommentCancel",
            }),
            new LabelCommand({
                label: () => this.data.label,
                action: () => this.action(),
                // telemetry: () => `Submission-${this.data.label}`,
            }),
        ];
    }

    protected get commentControl(): AbstractControl<string> { return this.formGroup.get("comment"); }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.dialogRef.updateSize("30vw");
        this.dialogRef.disableClose = true;

        this.commentControl.setValue(this.comment);
        subscribeToValueChanges(this.commentControl, (newValue: string) =>
        {
            this.comment = newValue;
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges
                .pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private action(): void
    {
        if (!this.data.nextIntent)
        {
            this.dialogRef.close(this.comment);
            return;
        }

        const dataElement: IInfoCardDataElement = {
            Label: "Comment",
            Value: this.comment,
            Action: undefined,
            ActionType: undefined,
            Color: undefined,
            Group: undefined,
            InfoCardAction: undefined,
            Name: undefined,
            Type: undefined,
            Visible: undefined,
        };

        const intentData: { "CardData": IInfoCardData; } = this.data.nextIntent.Data;
        if (!intentData || !("CardData" in intentData))
        {
            this.dialogRef.close(new Error("Intent has no InfoCardData"));
            return;
        }

        intentData.CardData.DataElements.push(dataElement);

        this.isProcessing = true;
        this.savingPromise = this.$intentService.resolveIntent(this.data.nextIntent)
            .then(
                r =>
                {
                    this.dialogRef.close(r);
                },
                errorResult =>
                {
                    this.dialogRef.close(
                        errorResult instanceof Error
                            ? errorResult
                            : new Error(errorResult));
                })
            .finally(() =>
            {
                this.isProcessing = false;
                this.savingPromise = undefined;
            });
    }

    private close(): void
    {
        this.dialogRef.close(DIALOG_CANCEL);
    }
}
